/**
 * Stable UUIDs for structural FHIR resources (AccessPolicies).
 *
 * These IDs are referenced by medplum-ui at runtime (e.g. POLICY_IDS in
 * src/features/rbac/constants.ts). They must not change between environments.
 *
 * Test fixture IDs (patients, appointments, etc.) live in the seed script.
 */

export const POLICY_IDS = {
  provider: '11111111-1111-4111-8111-111111111101',
  supervisor: '11111111-1111-4111-8111-111111111102',
  patientSelf: '11111111-1111-4111-8111-111111111103',
  guarantorBilling: '11111111-1111-4111-8111-111111111104',
  guarantorClinical: '11111111-1111-4111-8111-111111111105',
  clientGuarantor: '11111111-1111-4111-8111-111111111106',
  unifyAdmin: '11111111-1111-4111-8111-111111111107',
  unifyAssistant: '11111111-1111-4111-8111-111111111114',
  intakeLeadBot: '11111111-1111-4111-8111-111111111108',
  publicIntakeClient: '11111111-1111-4111-8111-111111111109',
  practiceAdmin: '11111111-1111-4111-8111-111111111110',
  practiceAssistant: '11111111-1111-4111-8111-111111111111',
  episodeClinical: '11111111-1111-4111-8111-111111111112',
  episodeBilling: '11111111-1111-4111-8111-111111111113',
} as const;

/** Logical key for a canonical AccessPolicy (matches POLICY_IDS keys). */
export type PolicyKey = keyof typeof POLICY_IDS;

/**
 * Stable `meta.tag` system for canonical AccessPolicies.
 *
 * Managed Medplum (api.medplum.com) rejects client-assigned resource ids unless
 * the project enables `update-as-create` (super-admin only). So we no longer
 * rely on the POLICY_IDS UUIDs existing on the server. Instead every canonical
 * AccessPolicy carries a `meta.tag` of { system: POLICY_TAG_SYSTEM, code:
 * <POLICY_TAG_CODES[key]> }, and callers resolve the server-assigned id at
 * runtime by searching `_tag`. (AccessPolicy has no `identifier` element, so a
 * tag is the only portable stable handle.) POLICY_IDS remains the
 * local/super-admin fallback where the fixed UUIDs exist.
 */
export const POLICY_TAG_SYSTEM = 'https://unify.health/fhir/access-policy';

/** Stable tag code per canonical AccessPolicy. */
export const POLICY_TAG_CODES: Record<PolicyKey, string> = {
  provider: 'provider',
  supervisor: 'supervisor',
  patientSelf: 'patient-self',
  guarantorBilling: 'guarantor-billing',
  guarantorClinical: 'guarantor-clinical',
  clientGuarantor: 'client-guarantor',
  unifyAdmin: 'unify-admin',
  unifyAssistant: 'unify-assistant',
  intakeLeadBot: 'intake-lead-bot',
  publicIntakeClient: 'public-intake-client',
  practiceAdmin: 'practice-admin',
  practiceAssistant: 'practice-assistant',
  episodeClinical: 'episode-clinical-template',
  episodeBilling: 'episode-billing-template',
} as const;

/** FHIR `meta.tag` coding for a canonical AccessPolicy, used for runtime id resolution. */
export function accessPolicyTag(key: PolicyKey): { system: string; code: string } {
  return { system: POLICY_TAG_SYSTEM, code: POLICY_TAG_CODES[key] };
}

/** Reverse lookup: canonical tag code → PolicyKey (used to migrate legacy fixed-UUID policies). */
export function policyKeyForTagCode(code: string): PolicyKey | undefined {
  return (Object.keys(POLICY_TAG_CODES) as PolicyKey[]).find((k) => POLICY_TAG_CODES[k] === code);
}

/** Bot AccessPolicies are tagged separately from human role policies. */
export const BOT_POLICY_TAG_SYSTEM = 'https://unify.health/fhir/bot-access-policy';

export const BOT_POLICY_TAG_CODES = {
  taskWriter: 'task-writer',
  portalSubmission: 'portal-submission',
  assessmentLifecycle: 'assessment-lifecycle',
} as const;

/**
 * Stable UUIDs for CodeSystem resources.
 * Seeded by cloud/seed-config or local/seed.
 */
export const CODE_SYSTEM_IDS = {
  // f0–f2 — kept out of the pilot Practitioner id block (…2201–2209) so local:seed
  // can PUT CodeSystems and Practitioners in one transaction without fullUrl collisions.
  profileRole: '22222222-2222-4222-8222-2222222222f0',
  practitionerRole: '22222222-2222-4222-8222-2222222222f1',
  therapyServiceType: '22222222-2222-4222-8222-2222222222f2',
} as const;
